import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import { useDoxaConfirm } from "@/components/doxa/DoxaConfirmDialog";
import {
  Archive,
  Bell,
  BellRing,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Info,
  ListChecks,
  Loader2,
  Megaphone,
  MoreHorizontal,
  Network,
  Pencil,
  Pin,
  Plus,
  RotateCcw,
  Search,
  Send,
  TriangleAlert,
  UserRoundCheck,
  Users,
  X,
} from "lucide-react";
import { useBusyKeys } from "@/hooks/useBusyKeys";
import { isScheduled, fmtDateTime, norm } from "@/components/RelationSelects";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader, PageLoading } from "@/components/doxa/AdminSurface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AUDIENCE_LABEL, CATEGORY_LABEL, CATEGORY_BADGE, tr, brDateTime } from "@/lib/labels";

const STATUS_OPTS = [
  { value: "active", label: "Ativos" },
  { value: "archived", label: "Arquivados" },
  { value: "all", label: "Todos" },
];

const CATEGORY_OPTIONS = [
  { value: "INFO", label: "Informativo" },
  { value: "EVENT", label: "Evento" },
  { value: "ACTION", label: "Ação" },
  { value: "URGENT", label: "Urgente" },
];

const AUDIENCE_OPTIONS = [
  { value: "SCHOOL", label: "Toda a escola" },
  { value: "CLASS_GROUP", label: "Turma específica" },
  { value: "CELL", label: "Célula específica" },
  { value: "STUDENTS", label: "Alunos específicos" },
];

const CATEGORY_META = {
  INFO: { label: "Informativo", icon: Info, rail: "bg-[#A9A39A]", iconClass: "bg-[#F3EFE7] text-[#6B655E]" },
  EVENT: { label: "Evento", icon: CalendarDays, rail: "bg-[#3E8E66]", iconClass: "bg-[#EAF5EE] text-[#2F7A45]" },
  ACTION: { label: "Ação", icon: ListChecks, rail: "bg-[#F29A3D]", iconClass: "bg-[#FFF2DE] text-[#B96712]" },
  URGENT: { label: "Urgente", icon: TriangleAlert, rail: "bg-[#D9534F]", iconClass: "bg-[#FCE8E7] text-[#B33A36]" },
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function audienceText(notice, classGroupMap = {}) {
  const audienceType = notice.audience_type || (notice.class_group_id ? "CLASS_GROUP" : "SCHOOL");
  if (audienceType === "CLASS_GROUP") return classGroupMap[notice.class_group_id] ? `Turma · ${classGroupMap[notice.class_group_id]}` : "Turma específica";
  if (audienceType === "CELL") return notice.audience_cell ? `Célula · ${notice.audience_cell}` : "Célula específica";
  if (audienceType === "STUDENTS") {
    const count = notice.audience_student_ids?.length || 0;
    return `${count} aluno${count === 1 ? "" : "s"} específico${count === 1 ? "" : "s"}`;
  }
  return AUDIENCE_LABEL[audienceType] || "Toda a escola";
}

function AudienceIcon({ type }) {
  if (type === "CELL") return <Network className="h-3.5 w-3.5 shrink-0" />;
  if (type === "STUDENTS") return <UserRoundCheck className="h-3.5 w-3.5 shrink-0" />;
  return <Users className="h-3.5 w-3.5 shrink-0" />;
}

export default function Notices() {
  const confirmAction = useDoxaConfirm();
  const { can } = usePermissions();
  const canManage = can("content.notices");
  const canPush = can("content.push");
  const { isBusy, run } = useBusyKeys();
  const [items, setItems] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [pushFor, setPushFor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get("/notices", { params: { status_filter: status } });
      setItems(data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [status]);
  useVisibleRefresh(() => load({ quiet: true }), { intervalMs: 0 });
  useEffect(() => { api.get("/class-groups").then(({ data }) => setClassGroups(data)).catch(() => {}); }, []);

  const classGroupMap = useMemo(() => Object.fromEntries(classGroups.map((group) => [group.id, group.name])), [classGroups]);
  const filtered = items.filter((notice) => {
    if (!q) return true;
    const text = [notice.title, notice.content, audienceText(notice, classGroupMap), classGroupMap[notice.class_group_id], tr(CATEGORY_LABEL, notice.category || "INFO")].join(" ");
    return norm(text).includes(norm(q));
  });

  const archive = (notice) => confirmAction({
    title: "Arquivar aviso?",
    description: <>O aviso <strong className="font-semibold text-[#1F1F1F]">{notice.title}</strong> sairá da visão de avisos ativos e continuará disponível entre os arquivados.</>,
    confirmLabel: "Arquivar",
    loadingLabel: "Arquivando…",
    variant: "warning",
    dataTestId: `confirm-archive-notice-${notice.id}`,
    onConfirm: async () => {
      await api.post(`/notices/${notice.id}/archive`);
      toast.success("Aviso arquivado");
      await load({ quiet: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const reactivate = (notice) => confirmAction({
    title: "Reativar aviso?",
    description: <>O aviso <strong className="font-semibold text-[#1F1F1F]">{notice.title}</strong> voltará para a visão de avisos ativos.</>,
    confirmLabel: "Reativar",
    loadingLabel: "Reativando…",
    variant: "positive",
    dataTestId: `confirm-reactivate-notice-${notice.id}`,
    onConfirm: async () => {
      await api.post(`/notices/${notice.id}/reactivate`);
      toast.success("Aviso reativado");
      await load({ quiet: true });
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const statusLabel = STATUS_OPTS.find((option) => option.value === status)?.label?.toLowerCase() || "na visão atual";

  if (loading && items.length === 0) return <PageLoading label="Carregando avisos…" />;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6 lg:p-8" data-testid="notices-page">
      <PageHeader
        eyebrow="Comunicação"
        title="Avisos"
        description="Comunique a escola com público, período e prioridade claros — e use push quando precisar chamar atenção no dispositivo."
        meta={`${items.length} aviso(s) ${statusLabel}`}
        action={canManage ? (
          <button type="button" onClick={() => setEditing("new")} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5" data-testid="new-notice-btn">
            <Plus className="h-4 w-4" /> Novo aviso
          </button>
        ) : null}
      />

      <section className="rounded-2xl border border-[#E0D9CD] bg-white p-3 sm:p-4" aria-label="Filtros de avisos">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex w-full rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-1 sm:w-auto" data-testid="notice-status-filter">
            {STATUS_OPTS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setStatus(option.value)}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition sm:flex-none ${status === option.value ? "bg-white text-[#D94A12] shadow-sm" : "text-[#6B655E] hover:text-[#1F1F1F]"}`}
                data-testid={`notice-status-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 lg:max-w-xl">
            <Search className="h-4 w-4 shrink-0 text-[#8A8378]" />
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Buscar por título, conteúdo, turma ou público…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#A59C90]"
              data-testid="notices-search"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="grid h-7 w-7 place-items-center rounded-lg text-[#8A8378] hover:bg-[#FFF0E9] hover:text-[#D94A12]" aria-label="Limpar busca" data-testid="notices-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="space-y-3">
        {filtered.map((notice) => {
          const category = CATEGORY_META[notice.category || "INFO"] || CATEGORY_META.INFO;
          const CategoryIcon = category.icon;
          const archived = notice.status === "ARCHIVED";
          const scheduled = isScheduled(notice.starts_at);
          const audienceType = notice.audience_type || (notice.class_group_id ? "CLASS_GROUP" : "SCHOOL");
          const actionBusy = isBusy(`archive:${notice.id}`) || isBusy(`reactivate:${notice.id}`);

          return (
            <article
              key={notice.id}
              className="relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white p-4 transition hover:border-[#D6CBBB] hover:shadow-[0_10px_30px_rgba(68,53,35,0.05)] sm:p-5"
              data-testid={`notice-row-${notice.id}`}
            >
              <span className={`absolute inset-y-0 left-0 w-1 ${category.rail}`} aria-hidden="true" />

              <div className="flex items-start gap-3 pl-1">
                <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${category.iconClass}`}>
                  <CategoryIcon className="h-5 w-5" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-[#A59C90]">{category.label}</p>
                      <div className="mt-0.5 flex min-w-0 items-center gap-2">
                        {notice.is_pinned && <Pin className="h-4 w-4 shrink-0 fill-[#F75A1B]/10 text-[#F75A1B]" aria-label="Fixado" />}
                        <h2 className="min-w-0 font-display text-lg font-bold leading-tight text-[#111111] sm:text-xl">{notice.title}</h2>
                      </div>
                    </div>
                    <NoticeRowMenu
                      notice={notice}
                      canManage={canManage}
                      busy={actionBusy}
                      onEdit={() => setEditing(notice)}
                      onArchive={() => run(`archive:${notice.id}`, () => archive(notice))}
                      onReactivate={() => run(`reactivate:${notice.id}`, () => reactivate(notice))}
                    />
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className={`doxa-badge ${CATEGORY_BADGE[notice.category || "INFO"]}`}>{tr(CATEGORY_LABEL, notice.category || "INFO")}</span>
                    {notice.is_pinned && <NoticeChip icon={Pin} tone="orange">Fixado</NoticeChip>}
                    {scheduled && <NoticeChip icon={Clock3} tone="warning">Agendado</NoticeChip>}
                    {archived && <NoticeChip icon={Archive}>Arquivado</NoticeChip>}
                    {notice.push_sent_at && <NoticeChip icon={BellRing} tone="success" testId={`notice-push-sent-${notice.id}`}>Push enviado</NoticeChip>}
                  </div>

                  <p className="mt-3 max-w-4xl whitespace-pre-wrap text-sm leading-relaxed text-[#514A42]">{notice.content}</p>

                  <div className="mt-4 grid gap-2 border-t border-[#EFE7DC] pt-3 text-xs text-[#6B655E] sm:grid-cols-2 lg:grid-cols-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="text-[#F75A1B]"><AudienceIcon type={audienceType} /></span>
                      <span className="truncate"><strong className="font-semibold text-[#4A4640]">Público:</strong> {audienceText(notice, classGroupMap)}</span>
                    </div>
                    {(notice.starts_at || notice.ends_at) && (
                      <div className="flex min-w-0 items-center gap-2">
                        <Clock3 className="h-3.5 w-3.5 shrink-0 text-[#8A8378]" />
                        <span className="truncate">
                          {notice.starts_at && notice.ends_at ? `${fmtDateTime(notice.starts_at)} → ${fmtDateTime(notice.ends_at)}` : notice.starts_at ? `A partir de ${fmtDateTime(notice.starts_at)}` : `Até ${fmtDateTime(notice.ends_at)}`}
                        </span>
                      </div>
                    )}
                    {notice.push_sent_at && (
                      <div className="flex min-w-0 items-center gap-2">
                        <Bell className="h-3.5 w-3.5 shrink-0 text-[#2F7A45]" />
                        <span className="truncate">{notice.push_total_sent || 0}/{notice.push_total_targets || 0} entregues · {brDateTime(notice.push_sent_at)}</span>
                      </div>
                    )}
                  </div>

                  {canPush && notice.status === "ACTIVE" && (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPushFor(notice)}
                        className="inline-flex items-center gap-2 rounded-xl border border-[#F3C7B7] bg-[#FFF7F2] px-3.5 py-2 text-xs font-semibold text-[#D94A12] transition hover:border-[#F75A1B] hover:bg-[#FFF0E9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
                        data-testid={`send-push-${notice.id}`}
                      >
                        <Send className="h-3.5 w-3.5" /> {notice.push_sent_at ? "Reenviar push" : "Enviar push"}
                      </button>
                      {notice.push_sent_at && <span className="text-[11px] text-[#8A8378]">O aviso continua visível no portal independentemente do push.</span>}
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}

        {!loading && items.length === 0 && (
          <section className="rounded-2xl border border-[#E0D9CD] bg-white">
            <EmptyState icon={Megaphone} title="Nenhum aviso nesta visão" description="Crie um aviso ou altere o filtro para consultar avisos arquivados." />
          </section>
        )}
        {!loading && items.length > 0 && filtered.length === 0 && (
          <section className="rounded-2xl border border-[#E0D9CD] bg-white">
            <EmptyState icon={Search} title="Nenhum aviso encontrado" description="Tente outro termo ou limpe a busca para ver todos os avisos desta visão." />
          </section>
        )}
      </div>

      {editing && (
        <NoticeModal
          notice={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load({ quiet: true }); }}
        />
      )}
      {pushFor && <SendPushModal notice={pushFor} classGroupMap={classGroupMap} onClose={() => setPushFor(null)} onSent={() => { setPushFor(null); load({ quiet: true }); }} />}
    </div>
  );
}

function NoticeChip({ icon: Icon, children, tone = "neutral", testId }) {
  const tones = {
    neutral: "border-[#E0D9CD] bg-[#FAF7F1] text-[#6B655E]",
    orange: "border-[#F3C7B7] bg-[#FFF7F2] text-[#D94A12]",
    warning: "border-[#F4D2A5] bg-[#FFF8EB] text-[#A85A09]",
    success: "border-[#BFDCC9] bg-[#EFF8F2] text-[#2F7A45]",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${tones[tone] || tones.neutral}`} data-testid={testId}>
      <Icon className="h-3 w-3" /> {children}
    </span>
  );
}

function NoticeRowMenu({ notice, canManage, busy, onEdit, onArchive, onReactivate }) {
  if (!canManage) return null;
  const archived = notice.status === "ARCHIVED";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
          aria-label={`Ações do aviso ${notice.title}`}
          data-testid={`notice-menu-${notice.id}`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-5 w-5" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações do aviso</DropdownMenuLabel>
        <DropdownMenuItem onSelect={onEdit} className="rounded-lg px-2.5 py-2 text-sm focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`edit-notice-${notice.id}`}>
          <Pencil className="mr-2 h-4 w-4" /> Editar aviso
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#EFE7DC]" />
        {archived ? (
          <DropdownMenuItem disabled={busy} onSelect={onReactivate} className="rounded-lg px-2.5 py-2 text-sm focus:bg-[#EFF8F2] focus:text-[#2F7A45]" data-testid={`reactivate-notice-${notice.id}`}>
            <RotateCcw className="mr-2 h-4 w-4" /> Reativar aviso
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled={busy} onSelect={onArchive} className="rounded-lg px-2.5 py-2 text-sm focus:bg-[#FAF7F1]" data-testid={`archive-notice-${notice.id}`}>
            <Archive className="mr-2 h-4 w-4" /> Arquivar aviso
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SendPushModal({ notice, classGroupMap, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/announcements/${notice.id}/push-preview`).then(({ data }) => setPreview(data)).catch((error) => toast.error(formatApiError(error)));
  }, [notice.id]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/announcements/${notice.id}/send-push`);
      toast.success(`Push enviado: ${data.total_sent} entregue(s), ${data.total_failed} falha(s), ${data.total_without_subscription} sem app.`);
      onSent();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-5" onClick={onClose}>
      <div className="w-full max-w-lg overflow-hidden rounded-[22px] border border-[#E0D9CD] bg-[#FBF8F2] shadow-[0_24px_80px_rgba(38,27,19,.24)]" onClick={(event) => event.stopPropagation()} data-testid="send-push-modal">
        <header className="flex items-start justify-between gap-4 border-b border-[#E5DDD2] bg-white px-5 py-4 sm:px-6">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><BellRing className="h-5 w-5" /></span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Notificação push</p>
              <h2 className="mt-0.5 font-display text-xl font-bold">{notice.push_sent_at ? "Reenviar aviso" : "Enviar aviso"}</h2>
              <p className="mt-1 text-xs text-[#6B655E]">{audienceText(notice, classGroupMap)}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12]" aria-label="Fechar"><X className="h-4 w-4" /></button>
        </header>

        <div className="p-4 sm:p-6">
          {!preview ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-[#6B655E]"><Loader2 className="h-4 w-4 animate-spin text-[#F75A1B]" /> Calculando destinatários…</div>
          ) : (
            <>
              <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4" data-testid="push-preview">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`doxa-badge ${CATEGORY_BADGE[preview.category || notice.category || "INFO"]}`}>{tr(CATEGORY_LABEL, preview.category || notice.category || "INFO")}</span>
                  <span className="text-sm font-semibold text-[#1F1F1F]">{preview.title}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#6B655E]">{preview.content}</p>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <PushMetric label="Alunos ativos" value={preview.total_active_students} />
                  <PushMetric label="Com push" value={preview.with_active_subscription} tone="success" />
                  <PushMetric label="Sem push" value={preview.without_subscription} />
                </div>

                {preview.blocked > 0 && <p className="mt-3 rounded-xl bg-[#FFF8EB] px-3 py-2 text-xs text-[#A85A09]">{preview.blocked} aluno(s) bloquearam notificações no navegador.</p>}
              </section>

              {preview.with_active_subscription === 0 && <p className="mt-3 text-xs leading-relaxed text-[#A85A09]">Nenhum aluno deste público tem notificações ativas. O aviso continua visível no Portal do Aluno, mas ninguém receberá push agora.</p>}
              <p className="mt-3 text-xs text-[#8A8378]">Ao tocar na notificação, o aluno abre este aviso dentro do portal.</p>

              <div className="mt-5 flex gap-2">
                <button type="button" onClick={onClose} className="flex-1 rounded-xl border border-[#E0D9CD] bg-white py-2.5 text-sm font-semibold text-[#4A4640] transition hover:bg-[#FAF7F1]" data-testid="push-cancel">Cancelar</button>
                <button type="button" onClick={send} disabled={busy} className="doxa-btn-primary flex-1 py-2.5 text-sm" data-testid="push-confirm">{busy ? "Enviando…" : notice.push_sent_at ? "Reenviar push" : "Enviar push"}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PushMetric({ label, value, tone = "neutral" }) {
  const toneClass = tone === "success" ? "text-[#2F7A45]" : "text-[#111111]";
  return (
    <div className="rounded-xl bg-[#FAF7F1] px-3 py-3 text-center">
      <p className={`font-display text-xl font-bold ${toneClass}`}>{value || 0}</p>
      <p className="mt-0.5 text-[10px] leading-tight text-[#8A8378]">{label}</p>
    </div>
  );
}

function NoticeModal({ notice, onClose, onSaved }) {
  const isEdit = !!notice;
  const [classGroups, setClassGroups] = useState([]);
  const [cells, setCells] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentQuery, setStudentQuery] = useState("");
  const [form, setForm] = useState({
    title: notice?.title || "",
    content: notice?.content || "",
    category: notice?.category || "INFO",
    audience_type: notice?.audience_type || (notice?.class_group_id ? "CLASS_GROUP" : "SCHOOL"),
    class_group_id: notice?.class_group_id || "",
    audience_cell: notice?.audience_cell || "",
    audience_student_ids: notice?.audience_student_ids || [],
    starts_at: toLocalInput(notice?.starts_at),
    ends_at: toLocalInput(notice?.ends_at),
    is_pinned: notice?.is_pinned || false,
  });
  const [busy, setBusy] = useState(false);
  const inputClass = "w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#A59C90] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";

  useEffect(() => {
    api.get("/class-groups").then(({ data }) => setClassGroups(data)).catch(() => {});
    api.get("/cells").then(({ data }) => setCells(data)).catch(() => {});
    api.get("/students").then(({ data }) => setStudents(data)).catch(() => {});
  }, []);

  const visibleStudents = students.filter((student) => !studentQuery || norm(student.full_name).includes(norm(studentQuery)));

  const toggleStudent = (id) => {
    setForm((current) => ({
      ...current,
      audience_student_ids: current.audience_student_ids.includes(id)
        ? current.audience_student_ids.filter((studentId) => studentId !== id)
        : [...current.audience_student_ids, id],
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        content: form.content,
        category: form.category,
        audience_type: form.audience_type,
        class_group_id: form.audience_type === "CLASS_GROUP" ? form.class_group_id : null,
        audience_cell: form.audience_type === "CELL" ? form.audience_cell : null,
        audience_student_ids: form.audience_type === "STUDENTS" ? form.audience_student_ids : [],
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        is_pinned: form.is_pinned,
      };
      if (isEdit) await api.put(`/notices/${notice.id}`, payload);
      else await api.post("/notices", payload);
      toast.success(isEdit ? "Aviso atualizado!" : "Aviso criado!");
      onSaved();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-5" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-[22px] border border-[#E0D9CD] bg-[#FBF8F2] shadow-[0_24px_80px_rgba(38,27,19,.24)]" onClick={(event) => event.stopPropagation()} data-testid="notice-modal">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[#E5DDD2] bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Comunicação</p>
            <h2 className="mt-0.5 font-display text-xl font-bold">{isEdit ? "Editar aviso" : "Novo aviso"}</h2>
            <p className="mt-1 text-xs text-[#6B655E]">Defina mensagem, público e período de exibição no Portal do Aluno.</p>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12]" aria-label="Fechar"><X className="h-4 w-4" /></button>
        </header>

        <form onSubmit={submit} className="space-y-4 p-4 sm:p-6">
          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Conteúdo</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Escreva o aviso e escolha a categoria que melhor representa a mensagem.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Título</label>
                <input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={inputClass} data-testid="notice-title" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Conteúdo</label>
                <textarea required value={form.content} onChange={(event) => setForm({ ...form, content: event.target.value })} rows={5} className={inputClass} data-testid="notice-content" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Categoria</label>
                <DoxaSelect value={form.category} onValueChange={(value) => setForm({ ...form, category: value })} options={CATEGORY_OPTIONS} className="w-full" testId="notice-category" ariaLabel="Categoria do aviso" />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Público</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Escolha quem verá o aviso. O backend continua validando o público selecionado.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Público-alvo</label>
                <DoxaSelect value={form.audience_type} onValueChange={(value) => setForm({ ...form, audience_type: value })} options={AUDIENCE_OPTIONS} className="w-full" testId="notice-audience" ariaLabel="Público-alvo do aviso" />
              </div>

              {form.audience_type === "CLASS_GROUP" && (
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Turma</label>
                  <DoxaSelect
                    value={form.class_group_id}
                    onValueChange={(value) => setForm({ ...form, class_group_id: value })}
                    options={[{ value: "", label: "Selecione a turma…" }, ...classGroups.map((group) => ({ value: group.id, label: group.name }))]}
                    className="w-full"
                    testId="notice-class"
                    ariaLabel="Turma do aviso"
                  />
                </div>
              )}

              {form.audience_type === "CELL" && (
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Célula</label>
                  <DoxaSelect
                    value={form.audience_cell}
                    onValueChange={(value) => setForm({ ...form, audience_cell: value })}
                    options={[{ value: "", label: "Selecione a célula…" }, ...cells.map((cell) => ({ value: cell.name, label: cell.name }))]}
                    className="w-full"
                    testId="notice-cell"
                    ariaLabel="Célula do aviso"
                  />
                </div>
              )}

              {form.audience_type === "STUDENTS" && (
                <div className="sm:col-span-2" data-testid="notice-students">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="text-xs font-semibold text-[#4A4640]">Alunos específicos</label>
                    <span className="text-[11px] text-[#8A8378]">{form.audience_student_ids.length} selecionado(s)</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] px-3 py-2">
                    <Search className="h-3.5 w-3.5 text-[#8A8378]" />
                    <input value={studentQuery} onChange={(event) => setStudentQuery(event.target.value)} placeholder="Buscar aluno…" className="min-w-0 flex-1 bg-transparent text-xs outline-none" />
                  </div>
                  <div className="mt-2 max-h-52 space-y-1 overflow-y-auto rounded-xl border border-[#E0D9CD] bg-white p-2">
                    {visibleStudents.map((student) => {
                      const checked = form.audience_student_ids.includes(student.id);
                      return (
                        <label key={student.id} className={`flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition ${checked ? "bg-[#FFF7F2] text-[#D94A12]" : "hover:bg-[#FAF7F1]"}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleStudent(student.id)} className="accent-[#F75A1B]" data-testid={`notice-student-${student.id}`} />
                          <span className="min-w-0 flex-1 truncate">{student.full_name}</span>
                          {checked && <CheckCircle2 className="h-4 w-4 shrink-0 text-[#F75A1B]" />}
                        </label>
                      );
                    })}
                    {visibleStudents.length === 0 && <p className="px-2 py-5 text-center text-xs text-[#8A8378]">Nenhum aluno encontrado.</p>}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Disponibilidade</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Defina quando o aviso aparece e se ele deve permanecer destacado no topo.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Início <span className="font-normal text-[#8A8378]">(opcional)</span></label>
                <input type="datetime-local" value={form.starts_at} onChange={(event) => setForm({ ...form, starts_at: event.target.value })} className={inputClass} data-testid="notice-starts" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Encerra em <span className="font-normal text-[#8A8378]">(opcional)</span></label>
                <input type="datetime-local" value={form.ends_at} onChange={(event) => setForm({ ...form, ends_at: event.target.value })} className={inputClass} data-testid="notice-ends" />
              </div>
            </div>

            <label className={`mt-4 flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${form.is_pinned ? "border-[#F3C7B7] bg-[#FFF7F2]" : "border-[#E0D9CD] bg-[#FAF7F1] hover:border-[#CFC5B7]"}`}>
              <input type="checkbox" checked={form.is_pinned} onChange={(event) => setForm({ ...form, is_pinned: event.target.checked })} className="mt-0.5 accent-[#F75A1B]" data-testid="notice-pinned" />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-[#4A4640]"><Pin className="h-3.5 w-3.5 text-[#F75A1B]" /> Fixar no topo</span>
                <span className="mt-0.5 block text-xs text-[#8A8378]">Use para comunicações que precisam continuar visíveis com prioridade.</span>
              </span>
            </label>
          </section>

          <div className="rounded-2xl border border-[#E7DED2] bg-[#FAF7F1] px-4 py-3 text-xs leading-relaxed text-[#6B655E]">
            O aviso ficará disponível no Portal do Aluno conforme público e período. Depois de salvar, use <strong className="font-semibold text-[#4A4640]">Enviar push</strong> na lista para alertar apenas quem tiver notificações ativas.
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-[#E0D9CD] bg-white px-4 py-2.5 text-sm font-semibold text-[#4A4640] transition hover:bg-[#FAF7F1]">Cancelar</button>
            <button type="submit" disabled={busy} className="doxa-btn-primary min-w-[150px] px-4 py-2.5 text-sm" data-testid="notice-submit">{busy ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar aviso"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
